/**
 * Builder chrome icons — the EXACT Shopify icons via `@shopify/polaris-icons`.
 *
 * The hand-drawn inline SVGs that used to live here read "old" next to Shopify's admin. We now re-export
 * the real Polaris glyphs (the same `viewBox="0 0 20 20"`, `currentColor`, filled-path icons Shopify
 * ships) wrapped in a tiny `<Glyph>` that fixes the rendered size (default 18-20px) and marks the icon
 * `aria-hidden` (the surrounding button/label carries the accessible name).
 *
 * The package is the SVG-only icon set (tree-shakeable — we import ONLY the icons we use here), NOT the
 * heavy `@shopify/polaris` component library, so bundle cost stays tiny. Each export keeps the SAME name
 * the rest of the chrome already imports, so no call-site churn.
 */
import type { ComponentType, SVGProps } from 'react';
import {
  ArrowLeftIcon as PArrowLeftIcon,
  ButtonIcon,
  CartIcon,
  CashDollarIcon,
  ChevronDownIcon as PChevronDownIcon,
  ChevronRightIcon as PChevronRightIcon,
  CodeIcon,
  CollectionIcon,
  CreditCardIcon,
  ColorIcon,
  CursorIcon,
  DeleteIcon,
  HashtagIcon,
  LayoutBlockIcon,
  ListBulletedIcon,
  ShieldCheckMarkIcon,
  StarFilledIcon,
  TextBlockIcon,
  VariantIcon,
  DuplicateIcon as PDuplicateIcon,
  DesktopIcon as PDesktopIcon,
  HideIcon,
  HomeIcon as PHomeIcon,
  ImageIcon,
  LayoutHeaderIcon,
  LayoutSectionIcon,
  MagicIcon,
  MenuHorizontalIcon,
  MobileIcon as PMobileIcon,
  PlusCircleIcon as PPlusCircleIcon,
  PlusIcon as PPlusIcon,
  ProductIcon as PProductIcon,
  RedoIcon as PRedoIcon,
  SearchIcon as PSearchIcon,
  SettingsIcon as PSettingsIcon,
  TabletIcon as PTabletIcon,
  StoreOnlineIcon,
  TextIcon,
  ThemeStoreIcon,
  ThemeTemplateIcon,
  UndoIcon as PUndoIcon,
  ViewIcon,
  XIcon as PXIcon,
} from '@shopify/polaris-icons';

type PolarisIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Wrap a Polaris icon at a fixed pixel size. Polaris glyphs are `viewBox="0 0 20 20"` filled paths that
 * inherit `currentColor`; we set width/height so they read crisp at the chrome's icon scale and keep
 * `display:block` so the surrounding flex button centres them cleanly. Default 20 matches Shopify's
 * admin icon scale; call sites that previously asked for 14-16px keep passing `size`.
 */
function glyph(Icon: PolarisIcon, defaultSize = 20) {
  function Wrapped({ size = defaultSize }: { size?: number }) {
    return (
      <Icon
        width={size}
        height={size}
        aria-hidden="true"
        focusable={false}
        style={{ display: 'block' }}
      />
    );
  }
  Wrapped.displayName = `Glyph(${Icon.displayName ?? Icon.name ?? 'Icon'})`;
  return Wrapped;
}

/* ── Top-bar nav (sidebar icon-rail) ──────────────────────────────────────── */

/** "Sections" tab — a layout/sections glyph. */
export const SectionsIcon = glyph(LayoutSectionIcon);
/** "Theme settings" tab — gear. */
export const SettingsIcon = glyph(PSettingsIcon);

/* ── Top-bar right + shared affordances ───────────────────────────────────── */

/** Back / exit arrow. */
export const ArrowLeftIcon = glyph(PArrowLeftIcon);
/** Chevron down — page selector / collapsibles. */
export const ChevronDownIcon = glyph(PChevronDownIcon, 16);
/** Chevron right — collapsed group / expandable row. */
export const ChevronRightIcon = glyph(PChevronRightIcon, 16);
/** Search magnifier. */
export const SearchIcon = glyph(PSearchIcon, 16);
/** Desktop monitor — device toggle. */
export const DesktopIcon = glyph(PDesktopIcon, 18);
/** Phone — device toggle. */
export const MobileIcon = glyph(PMobileIcon, 18);
/** Tablet — de-emphasised device toggle option. */
export const TabletIcon = glyph(PTabletIcon, 18);
/** Undo (curved arrow). */
export const UndoIcon = glyph(PUndoIcon);
/** Redo (curved arrow). */
export const RedoIcon = glyph(PRedoIcon);
/** Eye — view / show section. */
export const EyeIcon = glyph(ViewIcon, 18);
/** Eye-off — section hidden. */
export const EyeOffIcon = glyph(HideIcon, 18);
/** Trash — section / block remove. */
export const TrashIcon = glyph(DeleteIcon, 16);
/** Horizontal ellipsis — "more" menu. */
export const MoreIcon = glyph(MenuHorizontalIcon);
/** Close (X) — dismiss a bottom sheet / panel on narrow viewports. */
export const XIcon = glyph(PXIcon, 18);
/** Plus — add. */
export const PlusIcon = glyph(PPlusIcon, 16);
/** Circled plus — Shopify "⊕ Add section / Add block" affordance. */
export const PlusCircleIcon = glyph(PPlusCircleIcon, 16);
/** Storefront / theme — centre theme badge. */
export const StoreIcon = glyph(ThemeStoreIcon, 16);
/** "Preview / View store" — an online-store glyph for the button that opens the live storefront in a new tab. */
export const ViewStoreIcon = glyph(StoreOnlineIcon, 16);
/** Home — page selector leading glyph. */
export const HomeIcon = glyph(PHomeIcon, 16);
/** Sidekick assistant — sparkle/magic glyph. */
export const SidekickIcon = glyph(MagicIcon, 18);
/** Inspector pointer / cursor-arrow — the click-to-inspect toggle. */
export const InspectorIcon = glyph(CursorIcon, 18);
/** Duplicate — copy a section. */
export const DuplicateIcon = glyph(PDuplicateIcon, 16);
/** Sparkle — "Ask for changes" AI affordance. */
export const SparkleIcon = glyph(MagicIcon, 16);

/* ── Section type glyphs (keyed off registry meta.icon) ───────────────────── */

export const TypeLayoutIcon = glyph(LayoutSectionIcon, 16);
export const TypeTextIcon = glyph(TextIcon, 16);
export const TypeImageIcon = glyph(ImageIcon, 16);
export const BlockIcon = glyph(LayoutSectionIcon, 14);

/* ── Theme-settings group glyphs ──────────────────────────────────────────── */

export const LogoIcon = glyph(LayoutHeaderIcon, 18);
export const ColorPaletteIcon = glyph(ColorIcon, 18);
export const TypographyIcon = glyph(TextIcon, 18);
export const PageGlyphIcon = glyph(ThemeTemplateIcon, 18);
export const ButtonsIcon = glyph(ButtonIcon, 18);
export const CartGlyphIcon = glyph(CollectionIcon, 18);
export const CustomCssIcon = glyph(CodeIcon, 18);

/* ── Page/template TYPE glyphs (page selector leading icon, keyed off route.template) ─────────── */

const ProductGlyph = glyph(PProductIcon, 16);
const CollectionGlyph = glyph(CollectionIcon, 16);
const CartGlyph = glyph(CartIcon, 16);
const CheckoutGlyph = glyph(CreditCardIcon, 16);

/**
 * Map a route `template` to its leading glyph for the page selector (Home/Product/Collection/Cart/
 * Search/404). Replaces the old hardcoded Home glyph so the button reads as the page it edits. Unknown
 * or `index` templates fall back to Home. Colocated with the polaris imports so the glyphs stay local.
 */
export function templatePageIcon(template: string | undefined, size = 16) {
  switch (template) {
    case 'product':
      return <ProductGlyph size={size} />;
    case 'collection':
      return <CollectionGlyph size={size} />;
    case 'cart':
      return <CartGlyph size={size} />;
    case 'checkout':
      return <CheckoutGlyph size={size} />;
    case 'search':
      return <SearchIcon size={size} />;
    case 'page':
      return <PageGlyphIcon size={size} />;
    case '404':
      return <PageGlyphIcon size={size} />;
    default:
      return <HomeIcon size={size} />;
  }
}

/** Map a registry meta.icon name to a section glyph (falls back to a generic block). */
export function sectionTypeIcon(icon: string | undefined, size = 16) {
  switch (icon) {
    case 'layout':
      return <TypeLayoutIcon size={size} />;
    case 'type':
      return <TypeTextIcon size={size} />;
    case 'image':
      return <TypeImageIcon size={size} />;
    default:
      return <BlockIcon size={size} />;
  }
}

/* ── Block type glyphs (left-rail block tree) ─────────────────────────────── */

const GroupIcon = glyph(LayoutBlockIcon, 15);
const PriceIcon = glyph(CashDollarIcon, 15);
const RatingIcon = glyph(StarFilledIcon, 15);
const VariantPickerIcon = glyph(VariantIcon, 15);
const QuantityIcon = glyph(HashtagIcon, 15);
const BuyButtonsIcon = glyph(CartIcon, 15);
const DescriptionIcon = glyph(TextBlockIcon, 15);
const TrustIcon = glyph(ShieldCheckMarkIcon, 15);
const CollapsibleIcon = glyph(ListBulletedIcon, 15);

/**
 * Map a block `type` to its rail glyph (Shopify-parity per-piece icons). Keeps the rail readable as a
 * structure tree — each child block reads as what it is, not a generic dot. Unknown types fall back to a
 * neutral block glyph so a newly-registered block still renders.
 */
export function blockTypeIcon(type: string, size = 15) {
  switch (type) {
    case 'group':
      return <GroupIcon size={size} />;
    case 'media-gallery':
      return <TypeImageIcon size={size} />;
    case 'title':
      return <TypeTextIcon size={size} />;
    case 'rating':
      return <RatingIcon size={size} />;
    case 'price':
      return <PriceIcon size={size} />;
    case 'variant-picker':
      return <VariantPickerIcon size={size} />;
    case 'quantity':
      return <QuantityIcon size={size} />;
    case 'buy-buttons':
      return <BuyButtonsIcon size={size} />;
    case 'description':
      return <DescriptionIcon size={size} />;
    case 'trust':
      return <TrustIcon size={size} />;
    case 'collapsible-row':
      return <CollapsibleIcon size={size} />;
    default:
      return <BlockIcon size={size} />;
  }
}
